/** Pure helpers for the Today screen of the Visitors Desk. */
import type { ChangeItem, LadderView, Rate } from '../api/visitorsDesk'
import type { NowResp } from '../api/visitors'
import { fmtInt } from './format'
import { fmtCredit } from './deskFormat'

const DASH = '—'

export interface LadderRowView {
  label: string; definition: string; people: number | null; widthPct: number
  up: number | null; down: number | null; isKey: boolean
}

const ENGAGED_DEFINITION = 'member, 4+ active days (web, issue or email)'

/** The six rungs for the ladder band: the five ladder rungs, then Engaged members.
 *  Bars are relative to the largest rung; Regular is the key rung. The API
 *  gives no up/down movement for Engaged members, so those stay null. */
export function ladderRows(view: LadderView): LadderRowView[] {
  const rows = view.rungs.map((r) => ({
    label: r.label, definition: r.definition, people: r.people, up: r.moved_up, down: r.moved_down, isKey: r.rung === 'regular',
  }))
  rows.push({ label: 'Engaged member', definition: ENGAGED_DEFINITION, people: view.engaged.people, up: null, down: null, isKey: false })
  const max = Math.max(0, ...rows.map((r) => r.people ?? 0))
  return rows.map((r) => ({ ...r, widthPct: max > 0 && r.people ? Math.round(((r.people ?? 0) / max) * 1000) / 10 : 0 }))
}

const pct1 = (r: number) => `${(Math.round(r * 1000) / 10).toFixed(1)}%`

const fmtCountOrCredit = (n: number) => (Number.isInteger(n) ? fmtInt(n) : fmtCredit(n))

function stepLine(name: string, s: Rate): string {
  const counts = s.numerator === null || s.denominator === null ? '' : ` (${fmtCountOrCredit(s.numerator)} of ${fmtInt(s.denominator)})`
  return `${name} ${s.rate === null || s.rate === undefined ? DASH : pct1(s.rate)}${counts}`
}

/** The step-up line items under the ladder. A rate without a base shows a dash and the counts. */
export function stepUpText(s: LadderView['step_up']): string[] {
  return [
    stepLine('Fly-by → Occasional', s.flyby_occasional),
    stepLine('Occasional → Regular', s.occasional_regular),
    stepLine('Regular → Member', s.regular_member),
    stepLine('Members active', s.member_engaged),
  ]
}

export interface NowPage { path: string; title: string | null; visitors: number; source: string | null; pub?: string }

/** Links from the Right-now table to the human visits reading that page in the last 5 minutes. */
export function nowLinks(pub: string, rows: NowPage[]): { label: string; href: string }[] {
  return rows.map((r) => ({
    label: r.title ?? r.path,
    href: `/visitors/${encodeURIComponent(r.pub ?? pub)}/sessions?exit=${encodeURIComponent(r.path)}&active=5&verdict=human`,
  }))
}

/** Add up the per-title Right-now answers (for All titles); pages stay per title. */
export function sumNow(parts: { pub: string; data: NowResp }[]): Omit<NowResp, 'top_pages'> & { top_pages: NowPage[] } {
  const out = { active_visitors: 0, unconfirmed: 0, members_in_issue: 0, in_checkout: 0, top_pages: [] as NowPage[] }
  for (const { pub, data } of parts) {
    out.active_visitors += data.active_visitors
    out.unconfirmed += data.unconfirmed
    out.members_in_issue += data.members_in_issue
    out.in_checkout += data.in_checkout
    for (const p of data.top_pages) out.top_pages.push({ ...p, pub })
  }
  out.top_pages.sort((a, b) => b.visitors - a.visitors)
  return out
}

/** A What-changed link, carrying the desk's period/compare/filters. The item's own query keys win;
 *  an item that sets filter pills (`f.*`) replaces the page's pills rather than adding to them. */
export function changeHref(item: Pick<ChangeItem, 'href'>, _pub: string, search: string): string {
  const [path, own = ''] = item.href.split('?')
  const merged = new URLSearchParams(search.replace(/^\?/, ''))
  const ownParams = new URLSearchParams(own)
  if ([...ownParams.keys()].some((k) => k.startsWith('f.'))) {
    for (const k of [...merged.keys()]) if (k.startsWith('f.')) merged.delete(k)
  }
  for (const [k, v] of ownParams) merged.set(k, v)
  const qs = merged.toString()
  return qs ? `${path}?${qs}` : path
}

/** Ladder-shape sparkline: heights 0-1 against the tallest value (fly-by dominates). */
export function shapeBars(shape: number[]): number[] {
  const max = Math.max(0, ...shape)
  return shape.map((v) => (max > 0 ? Math.round((Math.max(0, v) / max) * 1000) / 1000 : 0))
}
